import { type ContentType, type VideoType, useFileUpload } from 'lib/media-upload-file';
import type { Media } from 'models';
import { useMemo } from 'react';
import type { FormInstance } from 'ui';
import { useFormFileInitialValue } from '../useFormFileInitialValue';
import { useFormFileSubscribe } from '../useFormFileSubscribe';
import { useRemoveUpload } from './useRemoveUpload';
import { useRetryUpload } from './useRetryUpload';
import { useStartUpload } from './useStartUpload';

type P = {
  contentType: ContentType;
  form: FormInstance;
  formName: string;
  mediaData?: Media;
  ref: string;
  type: VideoType;
};

export const useFormFileUpload = ({ contentType, form, formName, mediaData, ref, type }: P) => {
  const {
    file,
    removeUpload: removeUploadFromContext,
    startUpload: initiateFileUpload,
  } = useFileUpload({ contentType, ref, videoType: type });

  const initialValue = useFormFileInitialValue({ file, mediaData, type });

  const startUpload = useStartUpload({
    form,
    formName,
    initiateFileUpload,
    type,
  });
  const retryUpload = useRetryUpload({ file });
  const removeUpload = useRemoveUpload({
    file,
    form,
    formName,
    mediaData,
    ref,
    removeUploadFromContext,
    type,
  });

  useFormFileSubscribe({ file, form, formName, type });

  return useMemo(
    () => ({
      file,
      initialValue,
      removeUpload,
      retryUpload,
      startUpload,
    }),
    [file, initialValue, startUpload, retryUpload, removeUpload],
  );
};
